import { forwardRef, useImperativeHandle } from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { INITIAL_SURVEY_QUESTION } from '../../constants/survey.constants';
import { createSurveyFormSchema, type CreateSurveyFormValues } from '../../types/survey.types';
import { SurveyBasicInfoSection } from './SurveyBasicInfoSection';
import { SurveyQuestionsSection } from './SurveyQuestionsSection';

export interface SurveyFormHandle {
  reset: (values?: CreateSurveyFormValues) => void;
}

interface SurveyFormProps {
  defaultValues?: Partial<CreateSurveyFormValues>;
  onSubmit: (values: CreateSurveyFormValues, form: UseFormReturn<CreateSurveyFormValues>) => void;
  onCancel: () => void;
  submitLabel: string;
  pendingLabel: string;
  isPending: boolean;
}

const INITIAL_VALUES: CreateSurveyFormValues = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  questions: [INITIAL_SURVEY_QUESTION],
};

export const SurveyForm = forwardRef<SurveyFormHandle, SurveyFormProps>(function SurveyForm(
  { defaultValues, onSubmit, onCancel, submitLabel, pendingLabel, isPending },
  ref,
) {
  const form = useForm<CreateSurveyFormValues>({
    resolver: zodResolver(createSurveyFormSchema),
    defaultValues: { ...INITIAL_VALUES, ...defaultValues },
    mode: 'onBlur',
  });

  const isDirty = form.formState.isDirty;
  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty && !isPending);

  useImperativeHandle(
    ref,
    () => ({
      reset: (values) => form.reset(values),
    }),
    [form],
  );

  function handleCancel() {
    askBeforeClose(onCancel);
  }

  function handleSubmit(values: CreateSurveyFormValues) {
    onSubmit(values, form);
  }

  function handleInvalid() {
    requestAnimationFrame(() => {
      const invalidEl = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (invalidEl) {
        invalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        className="flex flex-1 flex-col gap-6"
        aria-busy={isPending || undefined}
      >
        <fieldset disabled={isPending} className="contents">
          <SurveyBasicInfoSection />
          <SurveyQuestionsSection />
        </fieldset>

        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t bg-background/95 px-1 py-3 pb-[max(--spacing(3),env(safe-area-inset-bottom))] backdrop-blur">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending} className="gap-1.5">
            {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            {isPending ? pendingLabel : submitLabel}
          </Button>
        </div>
      </form>
      {discardChangesDialog}
    </FormProvider>
  );
});
